import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { type Paginated, type PurchaseOrderDetailDto, type PurchaseOrderSummaryDto } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { SortTh, Td, Th, Tr } from "@/components/ui/Table";
import { Stack } from "@/components/ui/Stack";
import { ErrorState } from "@/components/ui/States";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { usePurchaseOrders, useSuppliers, useSettings } from "@/api/hooks";
import { apiFetch, ApiClientError } from "@/lib/api";
import { ExportButton } from "@/components/domain/ExportButton";
import { formatMoney, formatQty, majorUnits } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
import { PO_STATUS_TONE } from "@/domain/status";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";
import { RowPrintButton } from "@/components/domain/RowPrintButton";
import { PoPrintPreview } from "@/components/purchaseOrders/PoPrintPreview";
import { Select } from "@/components/ui/Select";
import { FieldLabel } from "@/components/ui/Input";
import { referenceLabel } from "@/lib/salesReference";

export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  /**
   * Thông báo "đã tạo N đơn mua hàng" do PoPiLinkPanel gửi kèm khi điều hướng
   * sang đây. Trước đây không ai đọc nó, nên người dùng bấm tạo hàng loạt xong
   * chỉ thấy mình bị chuyển trang mà không biết đã tạo mấy đơn.
   *
   * Xoá khỏi history ngay sau khi đọc: tải lại trang mà thông báo hiện lại là
   * nói dối về một việc vừa xảy ra.
   */
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(
    typeof (location.state as { notice?: unknown } | null)?.notice === "string"
      ? ((location.state as { notice: string }).notice)
      : null,
  );
  useEffect(() => {
    if (notice && location.state) navigate({pathname:location.pathname,search:location.search},{replace:true,state:null});
  }, [notice, location.state, location.pathname, location.search, navigate]);
  const [preview,setPreview] = useState<PurchaseOrderDetailDto|null>(null);
  const suppliers = useSuppliers({q:""}); const settings = useSettings();
  const priority = list.searchParams.get("priority") ?? "";
  const supplierId = list.searchParams.get("supplierId") ?? "";
  const from = list.searchParams.get("fromDate") ?? "";
  const to = list.searchParams.get("toDate") ?? "";
  const dateMs = (value: string, end=false) => value ? new Date(`${value}T${end ? "23:59:59.999" : "00:00:00.000"}Z`).getTime() : undefined;
  const context = location.search;
  const status = list.searchParams.get("status") ?? "";
  const { data, isLoading, isFetching, error, refetch } = usePurchaseOrders({
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
    status: status || undefined, priority:priority||undefined, supplierId:supplierId||undefined, from:dateMs(from), to:dateMs(to,true),
    // Máy chủ sắp xếp, không phải trình duyệt: sắp một trang rồi gọi đó là
    // "tổng tiền cao nhất" là nói dối trên danh sách nhiều trang.
    sort: list.sort ? (list.dir === "asc" ? list.sort : `-${list.sort}`) : undefined,
  });
  useClampListPage(data?.total, list.clampPage);
  const rows = data?.data ?? [];
  const canCreate = user ? can("records:create") : false;
  const forbidden = error instanceof ApiClientError && error.status === 403;
  const sortProps = { activeKey: list.sort, dir: list.dir, onToggle: list.toggleSort };

  return (
    <PageBody className={LIST_PAGE_BODY}>
      {preview && <PoPrintPreview po={preview} onClose={()=>setPreview(null)}/>}
      {/* Dải trạng thái vừa là thống kê vừa là bộ lọc — hệ cũ có dải đếm nhưng
          bấm vào không làm gì; gộp hai thứ lại thì con số dẫn thẳng tới việc. */}
      {(data?.statusCounts?.length ?? 0) > 0 && (
        <div className="mb-2 flex shrink-0 flex-wrap gap-1.5">
          {[{ status: "", count: data!.statusCounts.reduce((sum,sc)=>sum+sc.count,0) }, ...data!.statusCounts].map((sc) => (
            <button
              key={sc.status || "all"}
              type="button"
              aria-pressed={status === sc.status}
              onClick={() => list.updateParam("status", sc.status || null)}
              className={
                "rounded-sm border px-2.5 py-1 text-xs transition-colors " +
                (status === sc.status
                  ? "border-brand bg-brand-tint text-brand-ink"
                  : "border-line bg-surface text-ink-soft hover:border-brand")
              }
            >
              {sc.status ? translateStatus(t, sc.status) : t("common.all")}{" "}
              <span className="font-mono tabular font-semibold">{sc.count}</span>
            </button>
          ))}
        </div>
      )}

      {notice && (
        <p className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-sm border border-[color:var(--color-ok)]/30 bg-ok-tint px-3 py-2 text-xs text-ink-soft">
          {notice}
          <button type="button" onClick={() => setNotice(null)} className="text-2xs text-brand underline">
            {t("common.close")}
          </button>
        </p>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...list.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          aria-label={t("common.search")}
          className="w-full max-w-xs"
        />
        <div className="ml-auto">
          <ExportButton<PurchaseOrderSummaryDto>
            name="purchase-orders"
            title={t("nav.purchaseOrders")}
            rows={rows}
            total={data?.total}
            disabled={isFetching}
            errorKey="purchaseOrders.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<PurchaseOrderSummaryDto>>("/purchase-orders", {
                query: {
                  q: list.q.trim() || undefined,
                  status: status || undefined,
                  priority: priority || undefined,
                  supplierId: supplierId || undefined,
                  from: dateMs(from),
                  to: dateMs(to, true),
                  sort: list.sort ? (list.dir === "asc" ? list.sort : `-${list.sort}`) : undefined,
                  page,
                  pageSize,
                },
                signal,
              })
            }
            columns={[
              { header: t("purchaseOrders.orderNo"), value: (r) => r.poNumber },
              { header: t("common.supplier"), value: (r) => r.supplierName },
              { header: t("common.fromPi"), value: (r) => (r.linkedPiNo ? referenceLabel(t, r.linkedPiNo) : null) },
              { header: t("common.date"), value: (r) => formatDate(r.orderDate) },
              { header: t("purchaseOrders.expectedDelivery"), value: (r) => formatDate(r.expectedDeliveryDate) },
              { header: t("common.priority"), value: (r) => r.priority },
              { header: t("common.status"), value: (r) => translateStatus(t, r.status) },
              { header: t("common.items"), value: (r) => r.itemCount },
              { header: t("common.currency"), value: (r) => r.currency },
              { header: t("common.total"), value: (r) => majorUnits(r.total) },
              { header: t("common.createdOn"), value: (r) => formatDate(r.createdAt) },
              { header: t("common.createdBy"), value: (r) => r.createdByName },
            ]}
          />
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => navigate(`/purchase-orders/new${context}`)}>
            <Plus className="size-4" /> {t("purchaseOrders.new")}
          </Button>
        )}
      </div>
      <div className="mt-3 flex shrink-0 flex-wrap items-end gap-3">
        <div className="w-64"><FieldLabel htmlFor="po-filter-supplier">{t("common.supplier")}</FieldLabel><Select id="po-filter-supplier" containerClassName="w-full" value={supplierId} onChange={e=>list.updateParam("supplierId",e.target.value)}><option value="">{t("common.all")}</option>{suppliers.data?.data.map(s=><option key={s.id} value={s.id}>{s.companyName}</option>)}</Select></div>
        <div className="w-36"><FieldLabel htmlFor="po-filter-priority">{t("common.priority")}</FieldLabel><Select id="po-filter-priority" containerClassName="w-full" value={priority} onChange={e=>list.updateParam("priority",e.target.value)}><option value="">{t("common.all")}</option>{(settings.data?.businessOperations.optionLists.poPriorities??[]).map(p=><option key={p} value={p}>{translateStatus(t,p)}</option>)}</Select></div>
        <div><FieldLabel htmlFor="po-from">{t("poWorkspace.dateFrom")}</FieldLabel><Input id="po-from" type="date" value={from} max={to||undefined} onChange={e=>list.updateParam("fromDate",e.target.value)}/></div>
        <div><FieldLabel htmlFor="po-to">{t("poWorkspace.dateTo")}</FieldLabel><Input id="po-to" type="date" value={to} min={from||undefined} onChange={e=>list.updateParam("toDate",e.target.value)}/></div>
        {(list.q||status||priority||supplierId||from||to) && <Button variant="ghost" size="sm" onClick={()=>navigate("/purchase-orders",{replace:true})}>{t("poWorkspace.clearFilters")}</Button>}
      </div>
      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("purchaseOrders.errorLoad")}
        errorNode={
          forbidden ? (
            <ErrorState message={t("errors.forbidden")} />
          ) : (
            <ErrorState message={t("purchaseOrders.errorLoad")} onRetry={() => refetch()} />
          )
        }
        onRetry={() => refetch()}
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th className="w-12">#</Th>
            <SortTh className="sticky left-0 z-20 min-w-40 bg-sunken" sortKey="poNumber" {...sortProps}>{t("purchaseOrders.orderNo")}</SortTh>
            <Th>{t("common.supplier")}</Th>
            <Th>{t("common.fromPi")}</Th>
            <SortTh sortKey="orderDate" {...sortProps}>{t("common.date")}</SortTh>
            <SortTh sortKey="expectedDeliveryDate" {...sortProps}>{t("purchaseOrders.expectedDelivery")}</SortTh>
            <Th>{t("common.priority")}</Th>
            <Th align="center">{t("common.status")}</Th>
            <Th align="center">{t("common.items")}</Th>
            {/* Cột tiền tệ riêng, giống danh sách PI: đọc một cột số dài mà
                phải nhìn ký hiệu ở đầu từng ô để biết đồng nào là mệt mắt, và
                ¥ dùng chung cho CNY lẫn JPY. */}
            <Th>{t("common.currency")}</Th>
            <SortTh align="right" sortKey="total" {...sortProps}>{t("common.total")}</SortTh>
            <Th>{t("products.created")}</Th>
            <Th align="right" className="sticky right-0 z-20 bg-sunken">{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyTableRow colSpan={13}>{t("common.noMatches")}</EmptyTableRow>}
          {rows.map((r, index) => (
            <Tr key={r.id} interactive className="group">
              <Td className="font-mono tabular text-ink-faint">{(list.page-1)*list.pageSize+index+1}</Td>
              <Td className="sticky left-0 z-10 bg-surface">
                <Link
                  to={r.sourcingCaseId?`/sourcing/orders/${r.id}`:`/purchase-orders/${r.id}${context}`}
                  className="flex items-center gap-2 font-mono tabular font-medium text-brand group-hover:underline"
                >
                  {r.poNumber}{r.sourcingCaseId&&<span className="ml-2 text-xs text-warn">{t('sourcing.sourceTag')}</span>}
                </Link>
              </Td>
              <Td className="min-w-56 max-w-80 whitespace-normal text-ink">{r.supplierName ?? "—"}</Td>
              <Td className="font-mono tabular text-xs text-ink-soft">{referenceLabel(t, r.linkedPiNo) || "—"}</Td>
              <Td className="font-mono tabular text-xs text-ink-soft">{formatDate(r.orderDate)}</Td>
              <Td className="font-mono tabular text-xs text-ink-soft">{formatDate(r.expectedDeliveryDate)}</Td>
              <Td className="text-xs text-ink-soft">{r.priority ? translateStatus(t, r.priority) : "—"}</Td>
              <Td align="center">
                <Badge tone={PO_STATUS_TONE[r.status] ?? "neutral"}>{translateStatus(t, r.status)}</Badge>
              </Td>
              {/* Số dòng + hai mã đầu tiên: một con số trần thì người mua phải
                  mở từng PO mới biết nó mua cái gì. */}
              <Td align="center" className="text-ink-soft">
                <Stack
                  monoTop
                  top={formatQty(r.itemCount)}
                  bottom={r.itemPreview ?? null}
                />
              </Td>
              <Td className="font-mono tabular text-xs text-ink-soft">{r.currency}</Td>
              <Td align="right" className="font-mono tabular font-medium text-ink">
                {formatMoney(r.total, r.currency)}
              </Td>
              <Td>
                <Stack monoTop top={formatDate(r.createdAt)} bottom={r.createdByName} />
              </Td>
              <Td align="right" className="sticky right-0 z-10 bg-surface">
                {r.sourcingCaseId?<Link className="text-xs text-brand underline" to={`/sourcing/orders/${r.id}`}>{t('sourcing.viewOrder')}</Link>:<>                <RowPrintButton
                  path={`/purchase-orders/${r.id}`}
                  print={(po: PurchaseOrderDetailDto)=>setPreview(po)}
                  label={`${t("poWorkspace.previewPrint")}: ${r.poNumber}`}
                /></>}

              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
